import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardMedia,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Close,
  CloudUpload,
  Delete,
  PlayCircle,
  VideoLibrary,
} from "@mui/icons-material";
import {
  deleteVideo,
  getManagerThumbnails,
  getManagerVideos,
  uploadVideo,
} from "../../context/Video";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_DURATION = 60; // seconds

export default function VideoManagementComponent() {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, videoId: null });
  const [deleting, setDeleting] = useState(false);

  // Video player dialog
  const [playerDialog, setPlayerDialog] = useState({ open: false, sasUrl: null, thumbnailUrl: null });
  const [fetchingVideo, setFetchingVideo] = useState(false);

  const fetchThumbnails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getManagerThumbnails();
      setThumbnails(data.thumbnails || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchThumbnails();
  }, []);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      showSnackbar("Only video files are allowed.", "error");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showSnackbar("Video must be 10 MB or less.", "error");
      e.target.value = "";
      return;
    }

    const url = URL.createObjectURL(file);
    const vid = document.createElement("video");
    vid.preload = "metadata";
    vid.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (vid.duration > MAX_DURATION) {
        showSnackbar("Video must be 1 minute or less.", "error");
        e.target.value = "";
        setSelectedFile(null);
      } else {
        setSelectedFile(file);
      }
    };
    vid.onerror = () => {
      URL.revokeObjectURL(url);
      setSelectedFile(file);
    };
    vid.src = url;
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      setUploading(true);
      setUploadProgress(true);
      await uploadVideo(selectedFile);
      showSnackbar("Video uploaded successfully.");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchThumbnails();
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || "Upload failed.", "error");
    } finally {
      setUploading(false);
      setUploadProgress(false);
    }
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openDeleteDialog = (videoId) => setDeleteDialog({ open: true, videoId });

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await deleteVideo(deleteDialog.videoId);
      setThumbnails((prev) => prev.filter((v) => v.videoId !== deleteDialog.videoId));
      showSnackbar("Video deleted successfully.");
      setDeleteDialog({ open: false, videoId: null });
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || "Delete failed.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const handlePlay = async (videoId) => {
    try {
      setFetchingVideo(true);
      const data = await getManagerVideos();
      const video = (data.videos || []).find((v) => v._id === videoId);
      if (!video) {
        showSnackbar("Could not load video.", "error");
        return;
      }
      setPlayerDialog({ open: true, sasUrl: video.sasUrl, thumbnailUrl: video.thumbnailUrl });
    } catch (err) {
      showSnackbar(err.response?.data?.error || err.message || "Failed to load video.", "error");
    } finally {
      setFetchingVideo(false);
    }
  };

  const handleClosePlayer = () => setPlayerDialog({ open: false, sasUrl: null, thumbnailUrl: null });

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      {/* Page title */}
      <Typography variant="h4" fontWeight="bold" mb={4} color={theme.palette.text.primary}>
        Video Management
      </Typography>

      {/* Upload section */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          backgroundColor: theme.palette.background.paper,
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold" mb={0.5} color={theme.palette.text.primary}>
          Upload New Video
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Max file size: <strong>10 MB</strong> · Max duration: <strong>1 minute</strong> ·
          Supported: mp4, mov, webm and other video formats.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            style={{ display: "none" }}
            id="video-file-input"
            onChange={handleFileSelect}
            disabled={uploading}
          />
          <label htmlFor="video-file-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUpload />}
              disabled={uploading}
            >
              Choose Video
            </Button>
          </label>

          {selectedFile && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                {selectedFile.name} &nbsp;({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={uploading}
                startIcon={
                  uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />
                }
              >
                {uploading ? "Uploading…" : "Upload"}
              </Button>
              <Button variant="text" color="error" onClick={handleCancelFile} disabled={uploading}>
                Cancel
              </Button>
            </>
          )}
        </Box>

        {uploadProgress && (
          <Box mt={2}>
            <LinearProgress />
          </Box>
        )}
      </Paper>

      {/* Video grid */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", height: "50vh", alignItems: "center" }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : thumbnails.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <VideoLibrary sx={{ fontSize: 56, color: theme.palette.text.secondary, opacity: 0.4, mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No videos uploaded yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Use the upload section above to add your first video.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {thumbnails.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.videoId}>
              <Card
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: 3,
                  borderRadius: 2,
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: 6 },
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia
                    component="img"
                    height="180"
                    image={item.thumbnailUrl}
                    alt="Video thumbnail"
                    sx={{ objectFit: "cover" }}
                  />
                  <Tooltip title="Play video">
                    <IconButton
                      onClick={() => handlePlay(item.videoId)}
                      disabled={fetchingVideo}
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "#fff",
                        backgroundColor: "rgba(0,0,0,0.5)",
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.72)" },
                      }}
                    >
                      {fetchingVideo ? (
                        <CircularProgress size={26} color="inherit" />
                      ) : (
                        <PlayCircle sx={{ fontSize: 40 }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>

                <CardActions
                  sx={{
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    backgroundColor: theme.palette.action.hover,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.createdAt).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </Typography>
                  <Tooltip title="Delete video">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => openDeleteDialog(item.videoId)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, videoId: null })}
        PaperProps={{ sx: { backgroundColor: theme.palette.background.paper } }}
      >
        <DialogTitle color={theme.palette.text.primary}>Delete Video</DialogTitle>
        <DialogContent>
          <DialogContentText color={theme.palette.text.secondary}>
            Are you sure you want to delete this video? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, videoId: null })}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video player dialog */}
      <Dialog
        open={playerDialog.open}
        onClose={handleClosePlayer}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: theme.palette.background.default } }}
      >
        <DialogTitle
          sx={{
            color: theme.palette.text.primary,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          Video Preview
          <IconButton onClick={handleClosePlayer} sx={{ color: theme.palette.text.secondary }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0, backgroundColor: "#000" }}>
          {playerDialog.sasUrl && (
            <Box
              component="video"
              controls
              autoPlay
              poster={playerDialog.thumbnailUrl}
              src={playerDialog.sasUrl}
              sx={{ width: "100%", maxHeight: "70vh", display: "block" }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
